import { skipToken } from '@reduxjs/toolkit/query'
import { useEffect, useMemo, useState } from 'react'

import { PartnerSummaryBlock } from 'clickNgo/entities/partner/ui/partnerSummaryBlock'
import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { ModalDialog } from 'core-components/library/modalDialog'
import type { IModalContent } from 'core-components/library/modalDialog/models/IModalContent'
import {
  type FinancialMetricMode,
  MetricModeSwitch,
  resolveFinancialMetricMode,
} from 'partner360/entities/financialMetricMode'
import { useGetKeyInitiativesQuery } from 'partner360/entities/keyInitiatives'

import { buildKeyInitiativesReviewSummary } from '../lib/buildKeyInitiativesReviewSummary'
import { formatCurrency } from '../lib/formatters'
import type { KeyInitiativesReviewModalProps, KeyInitiativesReviewTotals } from '../model/types'
import { useConfirmKeyInitiativesReview } from '../model/useConfirmKeyInitiativesReview'
import { keyInitiativesReviewTranslation } from '../translation'

import styles from './KeyInitiativesReviewModal.module.scss'

interface KeyInitiativesReviewModalContentProps
  extends IModalContent,
    Omit<KeyInitiativesReviewModalProps, 'show'> {}

const OverviewMetricCard = ({ label, value }: { label: string; value: string }) => (
  <div className={styles.metricCard}>
    <span className={styles.metricCardLabel}>
      <Translation text={label} span />
    </span>
    <span className={styles.metricCardValue}>{value}</span>
  </div>
)

const GroupMetric = ({ label, value }: { label: string; value: string }) => (
  <div className={styles.groupMetric}>
    <span className={styles.groupMetricLabel}>
      <Translation text={label} span />
    </span>
    <span className={styles.groupMetricValue}>{value}</span>
  </div>
)

const formatTotalsValue = (value: number | null) => formatCurrency(value)

const getGoalLabelKey = (metricMode: FinancialMetricMode) => {
  if (metricMode === 'ndp') {
    return keyInitiativesReviewTranslation.goalNdp
  }

  if (metricMode === 'tnet') {
    return keyInitiativesReviewTranslation.goalTNet
  }

  return keyInitiativesReviewTranslation.goalNet
}

const getGoalValue = (totals: KeyInitiativesReviewTotals, metricMode: FinancialMetricMode) => {
  if (metricMode === 'ndp') {
    return totals.goalNdp
  }

  if (metricMode === 'tnet') {
    return totals.goalTNet
  }

  return totals.goalNet
}

const getSortableGoalValue = (
  totals: KeyInitiativesReviewTotals,
  metricMode: FinancialMetricMode
) => getGoalValue(totals, metricMode) ?? Number.NEGATIVE_INFINITY

const getTotalsView = (totals: KeyInitiativesReviewTotals, metricMode: FinancialMetricMode) => ({
  count: String(totals.count),
  goal: formatTotalsValue(getGoalValue(totals, metricMode)),
})

const getStatusBadgeTone = (status: string) => {
  const normalizedStatus = status.trim().toLowerCase()

  if (normalizedStatus === 'new') {
    return 'new'
  }

  if (normalizedStatus === 'work in progress') {
    return 'progress'
  }

  if (normalizedStatus === 'needs attention') {
    return 'attention'
  }

  if (normalizedStatus === 'done') {
    return 'done'
  }

  return 'neutral'
}

const getStatusIconClassName = (status: string) => {
  const normalizedStatus = status.trim().toLowerCase()

  if (normalizedStatus === 'work in progress') {
    return 'kbase kbase-lvl-clock-alt'
  }

  if (normalizedStatus === 'needs attention') {
    return 'kbase kbase-warning-alt'
  }

  if (normalizedStatus === 'done') {
    return 'kbase kbase-check-circle-alt'
  }

  return 'kbase kbase-star-alt'
}

const getReviewProgramType = (isDistributor: boolean, offering?: string) => {
  if (isDistributor) {
    return undefined
  }

  const normalizedOffering = offering?.trim()

  return normalizedOffering || undefined
}

const KeyInitiativesReviewModalContent = ({
  defaultMetricMode,
  availableMetricModes,
  partnerId,
  isDistributor,
  offering,
  region,
  country,
  partnerName,
  locationId,
  currentFiscalYear,
  onGoToPlanning,
  onClose,
  onConfirmed,
}: KeyInitiativesReviewModalContentProps) => {
  const [metricMode, setMetricMode] = useState<FinancialMetricMode>(() =>
    resolveFinancialMetricMode(defaultMetricMode, availableMetricModes)
  )
  const { confirmReview, isConfirmingReview, hasConfirmReviewError } =
    useConfirmKeyInitiativesReview()
  const reviewProgramType = getReviewProgramType(isDistributor, offering)
  const queryArg =
    partnerId && offering && currentFiscalYear
      ? {
          partnerId,
          fYear: currentFiscalYear,
          programType: reviewProgramType,
        }
      : skipToken
  const { data = [], isLoading, isFetching, isError, refetch } = useGetKeyInitiativesQuery(queryArg)
  const summary = useMemo(() => buildKeyInitiativesReviewSummary(data), [data])
  const resolvedMetricMode = resolveFinancialMetricMode(metricMode, availableMetricModes)
  const totalsView = getTotalsView(summary.totals, resolvedMetricMode)
  const goalLabelKey = getGoalLabelKey(resolvedMetricMode)
  const sortedGroups = useMemo(
    () =>
      [...summary.groups].sort((left, right) => {
        const goalDifference =
          getSortableGoalValue(right.totals, resolvedMetricMode) -
          getSortableGoalValue(left.totals, resolvedMetricMode)

        if (goalDifference !== 0) {
          return goalDifference
        }

        return left.development.localeCompare(right.development)
      }),
    [resolvedMetricMode, summary.groups]
  )

  useEffect(() => {
    setMetricMode(resolveFinancialMetricMode(defaultMetricMode, availableMetricModes))
  }, [availableMetricModes, defaultMetricMode, offering, partnerId])

  const handleConfirmReview = async () => {
    const isConfirmed = await confirmReview({
      partnerId,
      fYear: currentFiscalYear,
      programType: reviewProgramType,
    })

    if (!isConfirmed) {
      return
    }

    onClose()
    await onConfirmed?.()
  }

  if (isLoading || isFetching) {
    return (
      <div className={styles.loadingShell}>
        <LoaderOverlay isVisible color="white" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className={styles.stateCard}>
        <div className={styles.errorMessage}>
          <Translation text={keyInitiativesReviewTranslation.loadError} span />
        </div>
        <Button type="button" size="sm" onClick={() => refetch()}>
          <Translation text={keyInitiativesReviewTranslation.retry} span />
        </Button>
      </div>
    )
  }

  return (
    <div className={styles.root}>
      <div className={styles.headerRow}>
        <PartnerSummaryBlock
          className={styles.partnerSummary}
          partnerName={partnerName}
          region={region}
          country={country}
          pid={partnerId}
          locationId={locationId}
        />

        <div className={styles.metricToolbar}>
          <MetricModeSwitch<FinancialMetricMode>
            selectedMode={resolvedMetricMode}
            availableModes={availableMetricModes}
            onModeChange={setMetricMode}
            backgroudn="bg-transparent"
          />
        </div>
      </div>

      <section className={styles.overviewSection}>
        <div className={styles.metricCards}>
          <OverviewMetricCard
            label={keyInitiativesReviewTranslation.totalInitiatives}
            value={totalsView.count}
          />
          <OverviewMetricCard label={goalLabelKey} value={totalsView.goal} />
        </div>
      </section>

      {hasConfirmReviewError && (
        <div className={styles.errorMessage}>
          <Translation text={keyInitiativesReviewTranslation.confirmError} span />
        </div>
      )}

      {summary.items.length === 0 ? (
        <div className={styles.stateCard}>
          <Translation text={keyInitiativesReviewTranslation.emptyState} span />
        </div>
      ) : (
        <div className={styles.groups}>
          {sortedGroups.map((group) => {
            const groupTotalsView = getTotalsView(group.totals, resolvedMetricMode)

            return (
              <section key={group.development} className={styles.groupCard}>
                <div className={styles.groupHeader}>
                  <div className={styles.groupTitleWrap}>
                    <span className={styles.groupLabel}>
                      <Translation
                        text={keyInitiativesReviewTranslation.strategicInitiative}
                        span
                      />
                    </span>
                    <span className={styles.groupTitle}>{group.development}</span>
                  </div>

                  <div className={styles.groupMetrics}>
                    <GroupMetric
                      label={keyInitiativesReviewTranslation.initiatives}
                      value={groupTotalsView.count}
                    />
                    <GroupMetric label={goalLabelKey} value={groupTotalsView.goal} />
                  </div>
                </div>

                <div className={styles.statusTable}>
                  <div className={styles.statusHeader}>
                    <span>
                      <Translation text={keyInitiativesReviewTranslation.status} span />
                    </span>
                    <span className={styles.statusValue}>
                      <Translation text={keyInitiativesReviewTranslation.initiatives} span />
                    </span>
                    <span className={styles.statusValue}>
                      <Translation text={goalLabelKey} span />
                    </span>
                  </div>

                  {group.statuses.map((statusGroup) => {
                    const statusTotalsView = getTotalsView(statusGroup.totals, resolvedMetricMode)

                    return (
                      <div
                        key={`${group.development}-${statusGroup.status}`}
                        className={styles.statusRow}
                      >
                        <span
                          className={`${styles.statusName} ${
                            styles[`statusName${getStatusBadgeTone(statusGroup.status)}`]
                          }`}
                        >
                          <i
                            className={`${getStatusIconClassName(statusGroup.status)} ${
                              styles.statusIcon
                            } ${styles[`statusIcon${getStatusBadgeTone(statusGroup.status)}`]}`}
                            aria-hidden="true"
                          />
                          {statusGroup.status}
                        </span>
                        <span className={styles.statusValue}>{statusTotalsView.count}</span>
                        <span className={styles.statusValue}>{statusTotalsView.goal}</span>
                      </div>
                    )
                  })}
                </div>
              </section>
            )
          })}
        </div>
      )}

      <div className={styles.actions}>
        <Button
          type="button"
          color="danger"
          variant="text"
          size="sm"
          className={styles.actionButton}
          disabled={!onGoToPlanning || isConfirmingReview}
          onClick={() => onGoToPlanning?.(resolvedMetricMode)}
        >
          <Translation text={keyInitiativesReviewTranslation.openPlanning} span />
        </Button>
        <Button
          type="button"
          size="sm"
          className={styles.actionButton}
          icon="kbase kbase-check"
          loading={isConfirmingReview}
          disabled={isConfirmingReview}
          onClick={handleConfirmReview}
        >
          <Translation text={keyInitiativesReviewTranslation.confirmReview} span />
        </Button>
      </div>
    </div>
  )
}

export const KeyInitiativesReviewModal = ({
  show,
  defaultMetricMode,
  availableMetricModes,
  partnerId,
  isDistributor,
  offering,
  region,
  country,
  partnerName,
  locationId,
  currentFiscalYear,
  onClose,
  onConfirmed,
  onGoToPlanning,
}: KeyInitiativesReviewModalProps) => {
  const reviewProgramType = getReviewProgramType(isDistributor, offering)

  return (
    <ModalDialog
      show={show}
      size="sm"
      isContainerRestricted={false}
      classes={{ window: styles.window, body: styles.body }}
      content={KeyInitiativesReviewModalContent}
      header={() => (
        <div className={styles.sectionTitle}>
          <Translation text={keyInitiativesReviewTranslation.title} span />
          {reviewProgramType ? ` - ${reviewProgramType}` : ''}
        </div>
      )}
      contentProps={{
        defaultMetricMode,
        availableMetricModes,
        partnerId,
        isDistributor,
        offering,
        region,
        country,
        partnerName,
        locationId,
        currentFiscalYear,
        onGoToPlanning,
        onClose,
        onConfirmed,
      }}
      onClose={onClose}
    />
  )
}
